import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { apex25HomeUrl, apex25LegalUrl } from "@/lib/apex25AppUrl";
import { cn } from "@/lib/utils";
import { BrandMark, PoweredByTheResume, useIsApex25Brand } from "./auth-brand";

const LEGAL_LINK_CLASS =
  "font-medium text-[var(--technical-ink)] underline underline-offset-4 hover:no-underline";

/**
 * The "by continuing" line under the sign-in and sign-up forms. theRecruit and Apex 25 share one set
 * of documents (on tryapex25.com in production), so every visitor sees the same links.
 */
const LegalNotice = () => (
  <p className="mt-8 max-w-[400px] text-center text-xs leading-relaxed text-[var(--technical-muted)]">
    By continuing, you agree to the Apex 25{" "}
    <a
      href={apex25LegalUrl("/terms")}
      target="_blank"
      rel="noopener noreferrer"
      className={LEGAL_LINK_CLASS}
    >
      Customer Terms of Service
    </a>{" "}
    and{" "}
    <a
      href={apex25LegalUrl("/privacy")}
      target="_blank"
      rel="noopener noreferrer"
      className={LEGAL_LINK_CLASS}
    >
      Privacy Policy
    </a>
    .
  </p>
);

type AuthMode = "sign-in" | "sign-up";

const COPY: Record<AuthMode, { eyebrow: string; headline: string; subtext: string }> = {
  "sign-in": {
    eyebrow: "Welcome back",
    headline: "Reach a trustworthy shortlist, faster.",
    subtext:
      "Sign in to pick up where you left off: sourcing, ranking, and moving candidates forward.",
  },
  "sign-up": {
    eyebrow: "Get started",
    headline: "Give your hiring team an edge.",
    subtext:
      "Create your workspace to start sourcing, ranking, and shortlisting candidates with AI-assisted evidence.",
  },
};

type Props = {
  mode: AuthMode;
  children: ReactNode;
};

export const AuthLayout = ({ mode, children }: Props) => {
  const copy = COPY[mode];
  const isApex25 = useIsApex25Brand();
  // Apex 25 lives on its own domain, so "Back home" has to leave this one.
  const homeUrl = isApex25 ? apex25HomeUrl() : "/";
  const isExternalHome = homeUrl.startsWith("http");

  return (
    <div
      className={cn(
        "technical-app-shell relative grid min-h-screen lg:grid-cols-[45%_55%]",
        isApex25 && "brand-apex25"
      )}
    >
      {isExternalHome ? (
        <a
          href={homeUrl}
          className="absolute right-6 top-6 z-10 text-sm font-medium text-[var(--technical-muted)] transition-colors hover:text-[var(--technical-ink)] lg:right-10 lg:top-8"
        >
          &larr; Back home
        </a>
      ) : (
        <Link
          to={homeUrl}
          className="absolute right-6 top-6 z-10 text-sm font-medium text-[var(--technical-muted)] transition-colors hover:text-[var(--technical-ink)] lg:right-10 lg:top-8"
        >
          &larr; Back home
        </Link>
      )}

      <div className="relative hidden overflow-hidden border-r border-[var(--technical-line)] bg-[var(--technical-paper)] lg:flex lg:flex-col lg:justify-between lg:p-12">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.4]"
          style={{
            backgroundImage:
              "linear-gradient(var(--technical-line) 1px, transparent 1px), linear-gradient(90deg, var(--technical-line) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
            maskImage: "radial-gradient(ellipse 80% 60% at 30% 30%, black 40%, transparent 90%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 80% 60% at 30% 30%, black 40%, transparent 90%)",
          }}
        />

        <BrandMark isApex25={isApex25} height={28} />

        <div className="relative mx-auto max-w-md text-center">
          <p className="mb-4 font-mono text-xs font-bold uppercase tracking-[0.08em] text-[var(--technical-amber)]">
            {copy.eyebrow}
          </p>
          <h1 className="mb-4 font-['Space_Grotesk',_Inter,_ui-sans-serif,_system-ui,_sans-serif] text-4xl font-bold leading-[1.05] text-[var(--technical-ink)]">
            {copy.headline}
          </h1>
          <p className="mx-auto max-w-sm text-base leading-relaxed text-[var(--technical-muted)]">
            {copy.subtext}
          </p>
        </div>

        {isApex25 ? (
          <PoweredByTheResume className="relative text-center" />
        ) : (
          <p className="relative text-center font-mono text-xs font-bold uppercase tracking-[0.08em] text-[var(--technical-muted)]">
            theRecruit
          </p>
        )}
      </div>

      <div className="flex flex-col items-center justify-center bg-white px-6 py-16 lg:px-16">
        <div className="mb-10 flex flex-col items-center gap-2 lg:hidden">
          <BrandMark isApex25={isApex25} height={26} />
          {isApex25 && <PoweredByTheResume />}
        </div>
        <div className="w-full max-w-[400px]">{children}</div>
        <LegalNotice />
      </div>
    </div>
  );
};
